import * as React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { USER_ROLES } from '@/constants/user-roles';
import { hasAnyAccountRoles, hasAnyRolesV2, hasCommunityRolesV2 } from '@/utils/has-roles';
import { Avatar } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowSquareOut as ArrowSquareOutIcon } from '@phosphor-icons/react/dist/ssr/ArrowSquareOut';
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';
import { Headset as HeadsetIcon } from '@phosphor-icons/react/dist/ssr/Headset';

import type { Account } from '@/types/v2/account';
import { AccountType } from '@/types/v2/account';
import type { Community } from '@/types/v2/community';
import type { NavItemConfig } from '@/types/v2/nav';
import type { NavColor } from '@/types/v2/settings';
import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { isNavItemActive } from '@/lib/v2/is-nav-item-active';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { usePathname } from '@/hooks/v2/use-pathname';
import { useSettings } from '@/hooks/v2/use-settings';
import { useUser } from '@/hooks/v2/use-user';
import { effectiveCommunityRoles } from '@/lib/nav-access';
import { RouterLink } from '@/components/core/v2/link';
import { Logo } from '@/components/core/v2/logo';
import type { ColorScheme } from '@/styles/theme-v2/theme/types';
import { CommunitiesSwitch } from '../communities-switch';
import { icons } from '../nav-icons';
import { navColorStyles } from './styles';

const logoColors = {
  dark: { blend_in: 'light', discrete: 'light', evident: 'light' },
  light: { blend_in: 'dark', discrete: 'dark', evident: 'light' },
} as Record<ColorScheme, Record<NavColor, 'dark' | 'light'>>;

export interface SideNavProps {
  color?: NavColor;
  items?: NavItemConfig[];
  bottomItems?: NavItemConfig[];
  user?: User;
  community?: Community;
  account?: Account;
}

export function SideNav({
  color = 'evident',
  items = [],
  user,
  community,
  account,
  bottomItems = [],
}: SideNavProps): React.JSX.Element {
  const pathname = usePathname();
  const {
    settings: { colorScheme = 'light' },
  } = useSettings();
  const { isFeatureEnabled } = useFeatureFlags();
  const { communities } = useUser();

  const styles = navColorStyles[colorScheme][color];
  const logoColor = logoColors[colorScheme][color];

  // Show the interactive community switcher when the user can meaningfully
  // change communities: they manage/sit on the board of the selected community,
  // they're an account admin/manager, OR they simply belong to more than one
  // community (switching between your own communities is just navigation).
  const canSwitchCommunities = Boolean(
    account?.type === AccountType.MANAGED &&
      community &&
      user &&
      (hasCommunityRolesV2(community, [COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.BOARD_MEMBER]) ||
        hasAnyAccountRoles(account, [ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]) ||
        (communities?.length ?? 0) > 1)
  );
  return (
    <Box
      sx={{
        ...styles,
        bgcolor: 'var(--SideNav-background)',
        borderRight: 'var(--SideNav-border)',
        color: 'var(--SideNav-color)',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        height: '100%',
        left: 0,
        position: 'fixed',
        top: 0,
        width: 'var(--SideNav-width)',
        zIndex: 'var(--SideNav-zIndex)',
      }}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        <div>
          <Box
            component={RouterLink}
            href={paths.home}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Logo color={logoColor} height={50} />
          </Box>
        </div>
        {canSwitchCommunities ? (
          <CommunitiesSwitch />
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              border: '1px solid var(--Workspaces-border-color)',
              borderRadius: '12px',
              px: 2,
              py: 1,
              boxShadow: (theme) => theme.shadows[1],
              background: (theme) => theme.palette.background.paper,
            }}
          >
            <Avatar src={community?.logo_url} variant="square">
              {community?.name
                .split(' ')
                .map((name: string) => name.charAt(0))
                .join('')
                .toUpperCase()}
            </Avatar>
            <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
              <Typography color="var(--Workspaces-title-color)" variant="caption">
                Your Community
              </Typography>
              <Typography
                color="var(--Workspaces-name-color)"
                sx={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
                variant="subtitle2"
              >
                {community?.name}
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>
      <Box
        component="nav"
        sx={{
          flex: '1 1 auto',
          overflowY: 'auto',
          p: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {renderNavGroups({ items, pathname, user, community, account, isFeatureEnabled })}
      </Box>
      <Box
        component="nav"
        sx={{
          overflowY: 'auto',
          p: 2,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {renderNavGroups({ items: bottomItems, pathname, user, community, account, isFeatureEnabled })}
      </Box>
      <Box
        component="a"
        href="https://hoaone.ai/help"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          alignItems: 'center',
          borderTop: '1px solid var(--SideNav-border, rgba(255,255,255,0.08))',
          color: 'var(--NavItem-color)',
          display: 'flex',
          gap: 1,
          mx: 2,
          mb: 2,
          mt: 0.5,
          p: '6px 16px',
          borderRadius: 1,
          textDecoration: 'none',
          opacity: 0.7,
          '&:hover': { opacity: 1, bgcolor: 'var(--NavItem-hover-background)' },
        }}
      >
        <HeadsetIcon fontSize="var(--icon-fontSize-md)" />
        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
          Contact Support
        </Typography>
      </Box>
    </Box>
  );
}

function renderNavGroups({
  items,
  pathname,
  user,
  community,
  account,
  isFeatureEnabled,
}: {
  items: NavItemConfig[];
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
  isFeatureEnabled: (flag: string) => boolean;
}): React.JSX.Element {
  const children = items.reduce((acc: React.ReactNode[], curr: NavItemConfig): React.ReactNode[] => {
    const { items: childItems, key, roles, communityRoles, accountRoles, ...item } = curr;
    const flags =
      typeof item.featureFlags === 'string'
        ? item.featureFlags.split(',')
        : Array.isArray(item.featureFlags)
          ? item.featureFlags
          : [];

    if (flags.length > 0) {
      const isEnabled = flags.some((flag) => isFeatureEnabled(flag));
      if (!isEnabled) return acc;
    }

    let itemHasAccess = true; // Default to true if no roles specified

    if (roles || accountRoles || communityRoles) {
      // Only check account roles if account with roles exists
      const itemHasAccountAccess =
        (roles || accountRoles) && account?.roles
          ? (roles || accountRoles)?.some((role) => account.roles?.includes(role as unknown as any))
          : false;
      const itemHasCommunityAccess =
        communityRoles && community?.roles
          ? communityRoles.some((role) => effectiveCommunityRoles(community.roles).includes(role as unknown as any))
          : false;

      // If user has account with roles, check both account and community roles
      if (account?.roles && (roles || accountRoles)) {
        itemHasAccess = itemHasAccountAccess || itemHasCommunityAccess;
      } else {
        // If no account roles exist on user, only check community roles
        itemHasAccess = communityRoles ? itemHasCommunityAccess : true;
      }
    }

    if (!itemHasAccess) return acc; // Skip if user doesn't have access

    acc.push(
      <Stack component="li" key={curr.key} spacing={1.5}>
        {curr.title ? (
          <div>
            <Typography sx={{ color: 'var(--NavGroup-title-color)', fontSize: '0.875rem', fontWeight: 500 }}>
              {curr.title}
            </Typography>
          </div>
        ) : null}
        <div>
          {renderNavItems({ depth: 0, items: curr.items, pathname, user, community, account, isFeatureEnabled })}
        </div>
      </Stack>
    );

    return acc;
  }, []);

  return (
    <Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}

function renderNavItems({
  depth = 0,
  items = [],
  pathname,
  community,
  user,
  account,
  isFeatureEnabled,
}: {
  depth: number;
  items?: NavItemConfig[];
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
  isFeatureEnabled: (flag: string) => boolean;
}): React.JSX.Element {
  const children = items.reduce((acc: React.ReactNode[], curr: NavItemConfig): React.ReactNode[] => {
    const { items: childItems, key, roles, communityRoles, accountRoles, ...item } = curr;
    const flags =
      typeof item.featureFlags === 'string'
        ? item.featureFlags.split(',')
        : Array.isArray(item.featureFlags)
          ? item.featureFlags
          : [];

    if (flags.length > 0) {
      const isEnabled = flags.some((flag) => isFeatureEnabled(flag));
      if (!isEnabled) return acc;
    }

    // Hide Discussion when community has discussion page disabled
    if (curr.key === 'discussion' && community?.discussion_page_enabled === false) return acc;

    let itemHasAccess = true; // Default to true if no roles specified

    if (roles || accountRoles || communityRoles) {
      // Only check account roles if account with roles exists
      const itemHasAccountAccess =
        (roles || accountRoles) && account?.roles
          ? (roles || accountRoles)?.some((role) => account.roles?.includes(role as unknown as any))
          : false;
      const itemHasCommunityAccess =
        communityRoles && community?.roles
          ? communityRoles.some((role) => effectiveCommunityRoles(community.roles).includes(role as unknown as any))
          : false;

      // If user has account with roles, check both account and community roles
      if (account?.roles && (roles || accountRoles)) {
        itemHasAccess = itemHasAccountAccess || itemHasCommunityAccess;
      } else {
        // If no account roles exist on user, only check community roles
        itemHasAccess = communityRoles ? itemHasCommunityAccess : true;
      }
    }

    if (!itemHasAccess) return acc; // Skip if user doesn't have access

    const forceOpen = childItems
      ? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
      : false;

    acc.push(
      <NavItem depth={depth} forceOpen={forceOpen} key={key} navKey={key} pathname={pathname} user={user} {...item}>
        {childItems ? renderNavItems({ depth: depth + 1, pathname, items: childItems, user, isFeatureEnabled }) : null}
      </NavItem>
    );

    return acc;
  }, []);

  return (
    <Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {children}
    </Stack>
  );
}

interface NavItemProps extends Omit<NavItemConfig, 'items'> {
  children?: React.ReactNode;
  depth: number;
  forceOpen?: boolean;
  navKey?: string;
  pathname: string;
  user?: User;
  community?: Community;
  account?: Account;
}

function NavItem({
  children,
  depth,
  disabled,
  external,
  forceOpen = false,
  href,
  icon,
  label,
  matcher,
  navKey,
  pathname,
  title,
}: NavItemProps): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(forceOpen);
  const active = isNavItemActive({
    disabled,
    external,
    href: href ?? '',
    matcher,
    pathname,
  });
  const Icon = icon ? icons[icon] : null;
  const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;
  const isBranch = children && !href;
  const showChildren = Boolean(children && open);

  return (
    <Box component="li" data-depth={depth} data-tour={navKey ? `nav-${navKey}` : undefined} sx={{ userSelect: 'none' }}>
      <Box
        {...(isBranch
          ? {
              onClick: (): void => {
                setOpen(!open);
              },
              onKeyUp: (event: React.KeyboardEvent<HTMLDivElement>): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setOpen(!open);
                }
              },
              role: 'button',
            }
          : {
              ...(href && !disabled
                ? {
                    component: external ? 'a' : RouterLink,
                    href,
                    target: external ? '_blank' : undefined,
                    rel: external ? 'noreferrer' : undefined,
                  }
                : { role: 'button' }),
            })}
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          color: 'var(--NavItem-color)',
          cursor: 'pointer',
          display: 'flex',
          flex: '0 0 auto',
          gap: 1,
          p: '6px 16px',
          position: 'relative',
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          ...(disabled && {
            bgcolor: 'var(--NavItem-disabled-background)',
            color: 'var(--NavItem-disabled-color)',
            cursor: 'not-allowed',
          }),
          ...(active && {
            bgcolor: 'var(--NavItem-active-background)',
            color: 'var(--NavItem-active-color)',
            ...(depth > 0 && {
              '&::before': {
                bgcolor: 'var(--NavItem-children-indicator)',
                borderRadius: '2px',
                content: '" "',
                height: '20px',
                left: '-14px',
                position: 'absolute',
                width: '3px',
              },
            }),
          }),
          ...(open && { color: 'var(--NavItem-open-color)' }),
          '&:hover': {
            ...(!disabled &&
              !active && {
                bgcolor: 'var(--NavItem-hover-background)',
                color: 'var(--NavItem-hover-color)',
              }),
          },
        }}
        tabIndex={0}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
          {Icon ? (
            <Icon
              fill={active ? 'var(--NavItem-icon-active-color)' : 'var(--NavItem-icon-color)'}
              fontSize="var(--icon-fontSize-md)"
              weight={forceOpen || active ? 'fill' : undefined}
            />
          ) : null}
        </Box>
        <Box sx={{ flex: '1 1 auto' }}>
          <Typography
            component="span"
            sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
          >
            {title}
          </Typography>
        </Box>
        {label ? <Chip color="primary" label={label} size="small" /> : null}
        {external ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ArrowSquareOutIcon color="var(--NavItem-icon-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
        {isBranch ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
            <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
          </Box>
        ) : null}
      </Box>
      {showChildren ? (
        <Box sx={{ pl: '24px' }}>
          <Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
        </Box>
      ) : null}
    </Box>
  );
}
